/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
import { forEach, isEmpty, mapKeys, mapValues } from "lodash-es";
import { walkLayout } from './getters';
import { vueDeepAssign } from '../vue';
import { compileLayoutExtraStyle } from '../styling';
const upgradeStrategies = [
/**
 * ver 0 -> 1
 *
 * 从 0.2.1 之前的版本过来的
 *
 * -
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
function upgrade_0_to_1(ctx, pagelet, pagelets) {
  walkLayout(pagelet.layout, ({
    layout
  }) => {
    var _layout$props;
    const {
      type
    } = layout;
    // 旧版的子页面片是单独存储的，而且只有这两个内置组件用到
    if ((type === 'w-array' || type === 'w-array-list') && (_layout$props = layout.props) !== null && _layout$props !== void 0 && _layout$props.pageletId) {
      const subPagelet = pagelets.find(x => x.id === layout.props.pageletId);
      if (subPagelet) {
        vueDeepAssign(layout, {
          props: {
            pageletId: undefined
          },
          scopedSlots: {
            item: null
          }
        });
        layout.scopedSlots.item = subPagelet;
        // 子页面片里 binding 加了前缀
        walkLayout(subPagelet.layout, ({
          layout
        }) => {
          if (layout.bindings) {
            layout.bindings = mapValues(layout.bindings, val => `item.${val}`);
          }
          if (layout.props) {
            layout.props = mapValues(layout.props, val => {
              if (typeof val !== 'string') return val;
              return val.replace(/{{(.+?)}}/g, (_, expr) => `{{${expr.replace(/data\./g, 'data.item.')}}}`);
            });
          }
        });
      }
    }
    // 图片组件的宽度和高度被改到 style 里了
    if (type === 'w-image' && layout.props) {
      vueDeepAssign(layout, {
        props: {
          width: undefined,
          height: undefined
        },
        style: {
          width: layout.props.width,
          height: layout.props.height
        }
      });
    }
  });
},
/**
 * ver 1 -> 2
 *
 * 从 0.2.1-rc5 过来的
 *
 * - 这个版本里，binding 存储的 `data.` 前缀又加回去了
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
function upgrade_1_to_2(ctx, pagelet) {
  walkLayout(pagelet.layout, ({
    layout
  }) => {
    if (layout.bindings) {
      layout.bindings = mapValues(layout.bindings, x => `data.${x}`);
    }
    if (layout.scopedSlots) {
      forEach(layout.scopedSlots, subPagelet => upgrade_1_to_2(ctx, subPagelet));
    }
  });
},
/**
 * ver 2 -> 3
 *
 * > 1.16.0
 *
 * 以前的版本是在 Layout 里声明一个事件ID，然后根据 meta 决定要不要用 asProp 的方式转换为 prop。
 * 新规则中，在 Layout 里改为这样表示
 *
 * - `click` 表示使用 vue 原生的 emit 那一套
 * - `:onClick` 没有前缀则直接绑定到 prop 上，也就是 asProp 那一套
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
function upgrade_2_to_3(ctx, pagelet) {
  walkLayout(pagelet.layout, ({
    layout
  }) => {
    if (layout.events) {
      layout.events = mapKeys(layout.events, (v, k) => {
        var _ctx$components$layou;
        const eventMeta = (_ctx$components$layou = ctx.components[layout.type]) === null || _ctx$components$layou === void 0 || (_ctx$components$layou = _ctx$components$layou.meta.events) === null || _ctx$components$layou === void 0 ? void 0 : _ctx$components$layou.find(x => x.id === k);
        const asProp = eventMeta === null || eventMeta === void 0 ? void 0 : eventMeta.asProp;
        if (asProp) return `:${asProp}`;
        return k;
      });
    }
    if (layout.scopedSlots) {
      forEach(layout.scopedSlots, subPagelet => upgrade_2_to_3(ctx, subPagelet));
    }
  });
},
/**
 * ver 3 -> 4
 *
 * > 大概1.65.0
 *
 * 样式模板的存储发生了变化，见 https://iwiki.woa.com/pages/viewpage.action?pageId=1655174046
 *
 * - `pagelet.styleSheets` 格式变了
 * - `layout.class` 不再冗余存储 `layout.styleSheetName` 的内容
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
function upgrade_3_to_4(ctx, rootPagelet) {
  const oldStyleSheets = {};
  const newStyleSheets = [];
  const processPagelet = pagelet => {
    // 1. import old stylesheets
    const olds = pagelet.styleSheets;
    if (olds) {
      delete pagelet.styleSheets;
      olds.forEach(sheet => {
        oldStyleSheets[sheet.className] = sheet;
        newStyleSheets.push({
          id: sheet.className,
          name: sheet.cnName || sheet.className,
          source: compileLayoutExtraStyle({
            '': {
              '': sheet.style.static,
              '&:hover': sheet.style.hover
            }
          })
        });
      });
    }
    // 2. process "hover" private stylesheet
    walkLayout(pagelet.layout, ({
      layout
    }) => {
      if (layout.scopedSlots) {
        forEach(layout.scopedSlots, subPagelet => processPagelet(subPagelet));
      }
      if (!isEmpty(layout.style)) {
        // 旧版本里写的样式，全都是直接 inline 的
        layout.inlineStyleProps = Object.keys(layout.style).map(x => `+${x}`);
      }
      if (layout.styleSheetName) {
        const newStyleSheetName = [];
        layout.class = String(layout.class || '').split(' ').filter(v => {
          if (v.startsWith('uc-private-')) {
            const privateSheetIndex = newStyleSheets.findIndex(x => x.id === v);
            if (privateSheetIndex !== -1) newStyleSheets.splice(privateSheetIndex, 1);
            const privateSheet = oldStyleSheets[v];
            if (privateSheet) layout.extraStyle = {
              '': {
                '&:hover': privateSheet.style.hover
              }
            };
            return false;
          }
          if (v.startsWith('uc-public-')) {
            newStyleSheetName.push(v);
            return false;
          }
          return true;
        }).join(' ');
        if (newStyleSheetName.length) layout.styleSheetName = newStyleSheetName;
        // eslint-disable-next-line eqeqeq
        else if (layout.styleSheetName != null) delete layout.styleSheetName;
      }
    });
  };
  processPagelet(rootPagelet);
  if (newStyleSheets.length > 0) rootPagelet.styleSheets = newStyleSheets;
}];
// eslint-disable-next-line @typescript-eslint/naming-convention
export const newestPageletVersion = upgradeStrategies.length;
/**
 * 升级 Pagelets 的数据内容，解决兼容性问题。
 *
 * 注意，会直接在 pagelets 上修改
 *
 * 如果返回了 null 则表示不需要升级
 */
export function upgradePagelets(ctx, pagelets, target = newestPageletVersion) {
  if (!pagelets.length) return null;
  const ucVersion = Math.min(...pagelets.map(x => x.ucVersion || 0));
  if (ucVersion >= target) return null; // 返回了 null 则表示不需要升级
  const ans = pagelets;
  for (let i = 0; i < target && i < newestPageletVersion; i++) {
    const upgradeStrategy = upgradeStrategies[i];
    ans.forEach(pagelet => {
      var _pagelet$ucVersion;
      if (((_pagelet$ucVersion = pagelet.ucVersion) !== null && _pagelet$ucVersion !== void 0 ? _pagelet$ucVersion : 0) <= i) {
        upgradeStrategy(ctx, pagelet, ans);
        pagelet.ucVersion = i + 1;
      }
    });
  }
  return ans;
}
//# sourceMappingURL=upgradePagelets.js.map
